import { useState, useReducer, useEffect } from 'react';
import { isNil } from '../../../../helpers/utils';
import { isSSR } from '@front10/utils';
import { mergeDeep } from '../../../../helpers/utils';

export const NOT_AVALIABLES_DAYS = -1;

const mapFilter = ({
  prePopulation,
  sorting,
  faresTransformationLogic,
  dateRange = {},
  rateTags = [],
  stayLength = {},
  isPrePopulation,
  budget,
  travelerInterest = {},
  ...restFilter
}) => {
  let filterModified = { ...prePopulation, ...restFilter };

  //INFO: If layout does not equals null prePopulationDynamic is enabled

  if (Boolean(restFilter?.layout)) {
    const override = prePopulation?.layoutSettings[restFilter?.layout]
      ? prePopulation.layoutSettings[restFilter?.layout]
      : {};
    filterModified = mergeDeep(filterModified, override);
  }

  //INFO: DateRange must be send always to the integration layer
  // if (faresTransformationLogic === 'histogram') {
  filterModified.dateRange = dateRange;
  // }

  if (budget) {
    filterModified.budget = { maximum: budget };
  }

  delete filterModified.rateTags;
  if (rateTags !== null && rateTags !== undefined && rateTags.length !== 0) {
    filterModified.rateTags = rateTags.map((tag) => (typeof tag === 'string' ? tag : tag.value));
  }

  delete filterModified.nightStayRange;

  const disabled = rateTags.find((element) =>
    typeof element === 'string'
      ? element === prePopulation.disabledStayLength
      : element.value === prePopulation.disabledStayLength
  );

  if (Object.keys(stayLength).length !== 0 && !disabled) {
    filterModified.nightStayRange = stayLength;
  }

  Object.keys(filterModified).forEach((key) => {
    const value = filterModified[key];
    if (isNil(value)) {
      delete filterModified[key];
    } else if (Array.isArray(value) && value[0] === null && value.length === 1) {
      // FIXME: This deletes arrays of the form [null], which are usually a product
      // of context evaluation. We should make context evaluation to be able to
      // handle this case correctly and not send this value.
      delete filterModified[key];
    }
  });

  return filterModified;
};

const states = {
  INITIAL: 'INITIAL',
  HIDDEN: 'HIDDEN',
  SKELETON: 'SKELETON',
  COMPLETE: 'COMPLETE',
  WAITING_FOR_PROPERTY: 'WAITING_FOR_PROPERTY',
  ERROR: 'ERROR',
  LOADING: 'LOADING',
  NO_RESULTS: 'NO_RESULTS'
};

let transitions = {
  [states.INITIAL]: {
    feed: states.COMPLETE,
    hide: states.HIDDEN,
    load: states.SKELETON,
    show_no_results: states.NO_RESULTS
  },
  [states.SKELETON]: {
    success: states.COMPLETE,
    failure: states.ERROR,
    show_no_results: states.NO_RESULTS,
    hide: states.HIDDEN
  },
  [states.COMPLETE]: {
    load: states.LOADING,
    wait_for_property: states.WAITING_FOR_PROPERTY
  },
  [states.WAITING_FOR_PROPERTY]: {
    load: states.LOADING,
    wait_for_property: states.WAITING_FOR_PROPERTY
  },
  [states.LOADING]: {
    success: states.COMPLETE,
    show_no_results: states.NO_RESULTS,
    failure: states.ERROR
  },
  [states.NO_RESULTS]: {
    load: states.LOADING,
    wait_for_property: states.WAITING_FOR_PROPERTY
  },
  [states.ERROR]: {
    load: states.LOADING,
    wait_for_property: states.WAITING_FOR_PROPERTY
  }
};

function reducer(prev, action) {
  const nextState = transitions[prev.value][action.transition];
  if (nextState) {
    return {
      ...prev,
      value: nextState,
      history: prev.value,
      ...action.payload
    };
  }
  return prev;
}

function useQuery({ getData, active = true, initialData }, deps = []) {
  const [firstLoad, setFirstLoad] = useState(initialData);

  useEffect(() => {
    if (!active) {
      return;
    }
    if (firstLoad && initialData) {
      setFirstLoad(false);
      return;
    }
    setFirstLoad(false);
    getData();
    // eslint-disable-next-line
  }, [active, ...deps]);
}

export function useFaresApi({
  getFares,
  prePopulation,
  dateRange: { start, end, flexibleDates, availableDays, selected },
  rateTags,
  stayLength,
  initialData,
  travelerInterest,
  selectedProperty,
  integrationSettings,
  faresTransformationLogic,
  showNoResultsPlaceholder = false,
  getResultsLength,
  minInitialResults,
  meta,
  pageContext,
  cache = false,
  cacheDuration = -1,
  enabledHistogram = false,
  layout,
  budget
}) {
  if (enabledHistogram) {
    transitions[states.SKELETON] = {
      success: states.COMPLETE,
      failure: states.ERROR,
      show_no_results: states.NO_RESULTS,
      hide: states.HIDDEN,
      wait_for_property: states.WAITING_FOR_PROPERTY
    };
  }
  let initialState = transitions[states.INITIAL].load;

  if (initialData) {
    const resultsLength = getResultsLength(initialData);
    if (resultsLength < minInitialResults) {
      initialState = transitions[states.INITIAL].hide;
    } else if (showNoResultsPlaceholder && resultsLength === 0) {
      initialState = transitions[states.INITIAL].show_no_results;
    } else {
      initialState = transitions[states.INITIAL].feed;
    }
  }
  const [{ value: state, data, history }, send] = useReducer(reducer, {
    value: initialState,
    data: initialData,
    history: undefined
  });

  const [loadedProperty, setLoadedProperty] = useState({});

  const getData = () => {
    //INFO: Add the enableHistogram to the validation to keep the container wait for the propertyValue for the containers where the property is required
    if (
      !Boolean(selectedProperty.value) &&
      transitions[state].wait_for_property &&
      enabledHistogram
    ) {
      return Promise.resolve().then(() => {
        send({ transition: 'wait_for_property' });
      });
    }

    if (transitions[state].load) {
      send({ transition: 'load' });
    }
    return getFares(
      mapFilter({
        prePopulation,
        faresTransformationLogic,
        dateRange: { start, end, flexibleDates, availableDays, selected },
        budget,
        travelerInterest,
        propertyCode: selectedProperty.value,
        rateTags,
        stayLength,
        isPrePopulation: state === 'SKELETON',
        layout
      }),
      integrationSettings,
      faresTransformationLogic,
      meta,
      pageContext,
      cache,
      cacheDuration
    )
      .then((_data) => {
        const itemCount = getResultsLength(_data);
        if (transitions[state].hide && itemCount < minInitialResults) {
          send({ transition: 'hide' });
        } else if (showNoResultsPlaceholder && itemCount === 0) {
          send({ transition: 'show_no_results', payload: { data: _data } });
        } else {
          send({ transition: 'success', payload: { data: _data } });
        }
        setLoadedProperty(selectedProperty);
      })
      .catch((err) => {
        send({ transition: 'failure' });

        setLoadedProperty(selectedProperty);
        throw err;
      });
  };

  useQuery(
    {
      getData,
      initialData
    },
    [
      faresTransformationLogic,
      integrationSettings,
      prePopulation,
      start,
      end,
      selectedProperty,
      stayLength,
      rateTags,
      start,
      end,
      flexibleDates,
      budget,
      layout,
      meta
    ]
  );

  function match(stateValue) {
    if (isSSR() && stateValue === 'SKELETON') {
      return true;
    }
    return stateValue === state || (state === states.LOADING && history === stateValue);
  }

  return {
    state,
    data,
    history,
    match,
    retry: () => getData(),
    loadedProperty
  };
}
